import { BreakpointService } from '@/common/services/breakpoint/breakpoint.service';
import { CurrentReservationService } from '@/common/services/reservation/current';
import {
    Discount,
    Vehicle,
    VehicleType,
} from '@/core/domain/branches/models/branch.model';
import { GtmReserveSearchUseCase } from '@/core/domain/gtm/usecases/gtmReserveSearch.usecase';
import { GtmViewItemListUseCase } from '@/core/domain/gtm/usecases/gtmViewItemList.usecase';
import { RestoreReservationUseCase } from '@/core/domain/reservation/usecases/restoreReservation.usecase';
import { updateReservation } from '@/core/reservation/store/reservation.actions';
import { selectReservation } from '@/core/reservation/store/reservation.selector';
import { selectVehicles } from '@/core/reservation/store/vehicles.selector';
import { ProgressBarComponent } from '@/ui/shared/components/progress-bar/progress-bar.component';
import { NgIfContext } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    HostListener,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { SwalComponent } from '@sweetalert2/ngx-sweetalert2';
import _ from 'lodash';
import { Observable, of, switchMap } from 'rxjs';

@Component({
    selector: 'fe-step-2',
    templateUrl: './step-2.component.html',
    styleUrls: ['./step-2.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class Step2Component implements OnInit, AfterViewInit {
    @ViewChild('deleteSwal')
    public readonly deleteSwal!: SwalComponent;
    public isSidebarVisible = false;
    public isMobile = false;
    public loading = true;
    public international = true;
    public rateWeekly = false;
    public currency?: string;
    public filtered = false;

    public vehicles: Vehicle[] = [];
    public originalOrder: Vehicle[] = [];
    public vehicles$!: Observable<any>;
    public reservation: any;

    public discounts: Discount[] = [];

    public rentalDays?: number;
    public freeDays?: number;
    public discountOffice?: Discount;

    public vehicleTypes: VehicleType[] = [];
    public selectedVehicleTypeId: number | null = null;
    private currentSort = 'featured';

    loadedContent: TemplateRef<NgIfContext<boolean>> | null | undefined;

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.isMobile = window.innerWidth <= 768;
    }

    @ViewChild('progressBarDesktop') progressBarDesktop!: ProgressBarComponent;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly store: Store,
        public breakpoint: BreakpointService,
        private readonly reserveSearchUseCase: GtmReserveSearchUseCase,
        private readonly viewItemListUseCase: GtmViewItemListUseCase,
        private readonly cdr: ChangeDetectorRef,
        private readonly currentReservation: CurrentReservationService,
        private readonly restore: RestoreReservationUseCase,
    ) {
        this.international = this.currentReservation.current.origin === 'INT';
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    ngOnInit(): void {
        this.restore
            .execute()
            .pipe(
                switchMap((response) => {
                    this.onRestoreReservation();
                    return of(response);
                }),
            )
            .subscribe();
    }

    private onRestoreReservation(): void {
        this.store
            .select(selectReservation)
            .pipe(
                switchMap((reservation) => {
                    this.international =
                        this.currentReservation.current.origin === 'INT';
                    this.reservation = reservation;
                    return of(reservation);
                }),
            )
            .subscribe();

        this.store.dispatch(
            updateReservation({
                value: {
                    startDate: this.reservation.startDate,
                    isUpdateCategory: false,
                },
            }),
        );

        this.store.select(selectVehicles).subscribe((response) => {
            this.international =
                this.currentReservation.current.origin === 'INT';
            this.vehicles = response.vehicles;
            if (!this.international) {
                this.onFeatured();
            } else {
                this.onSortChange('lowerPrice');
            }
            this.discounts = response.discounts || [];
            this.rentalDays = response.rentalDays;
            this.freeDays = response.freeDays;
            this.originalOrder = [...this.vehicles];
            this.vehicleTypes = response.vehicleTypes || [];
            this.selectedVehicleTypeId = null;

            if (response?.discounts) {
                this.discountOffice = response?.discounts[0];
                if (this.discountOffice?.availableUpgrade) {
                    const vehicleUpgrade = this.vehicles.find(
                        (vehicle) =>
                            vehicle.id === this.discountOffice?.returnCategory,
                    );
                    this.store.dispatch(
                        updateReservation({
                            value: {
                                upgradeCatergoryPromotion: vehicleUpgrade,
                            },
                        }),
                    );
                }
            }
            this.viewItemListUseCase.execute().subscribe();
        });
        this.reserveSearchUseCase.execute().subscribe();

        this.isMobile = window.innerWidth <= 768;
    }

    getDiscountForMethod(method: string): Discount | null {
        return (
            this.discounts.find((discount) => discount.method === method) ||
            null
        );
    }

    toggleSidebar() {
        this.isSidebarVisible = !this.isSidebarVisible;
        const bodyElement = document.querySelector('body');

        if (bodyElement) {
            if (this.isSidebarVisible) {
                bodyElement.classList.add('sidebar-visible');
                window.scrollTo(0, 0);
            } else {
                bodyElement.classList.remove('sidebar-visible');
            }
        } else {
            console.error('No se encontró el elemento <body>');
        }
    }

    onSortChange(option: string): void {
        this.currentSort = option;
        switch (option) {
            case 'featured':
                this.onFeatured();
                break;
            case 'lowerPrice':
                this.sortByLowerPrice();
                break;
            case 'higherPrice':
                this.sortByHigherPrice();
                break;
        }
    }

    onVehicleTypeChange(typeId: number | null): void {
        this.selectedVehicleTypeId = typeId;
        this.vehicles =
            typeId === null
                ? [...this.originalOrder]
                : this.originalOrder.filter(
                      (vehicle) => vehicle.vehicleTypeId === typeId,
                  );
        this.onSortChange(this.currentSort);
    }

    onFeatured(): void {
        this.filtered = true;
        this.vehicles = [...this.vehicles].sort((a, b) => {
            const featuredA = a.featured === 1 ? 0 : 1;
            const featuredB = b.featured === 1 ? 0 : 1;

            if (featuredA !== featuredB) {
                return featuredA - featuredB;
            }

            const rentRateA = a.charges.office?.rentRate ?? 0;
            const rentRateB = b.charges.office?.rentRate ?? 0;

            return rentRateA - rentRateB;
        });
    }

    sortByLowerPrice(): void {
        this.filtered = true;
        this.vehicles = [...this.vehicles].sort((a, b) => {
            const rentRateA = a.charges.office?.rentRate ?? 0;
            const rentRateB = b.charges.office?.rentRate ?? 0;

            return rentRateA - rentRateB;
        });
    }

    sortByHigherPrice(): void {
        this.filtered = true;
        this.vehicles = [...this.vehicles].sort((a, b) => {
            const rentRateA = a.charges.office?.rentRate ?? 0;
            const rentRateB = b.charges.office?.rentRate ?? 0;

            return rentRateB - rentRateA;
        });
    }
}
